/**
 * HIPAA Compliance Service
 * Handles audit logging, data encryption, and PHI protection
 */

import { fetchAuthSession } from 'aws-amplify/auth';

class HIPAAComplianceService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.auditQueue = []; // Temporary queue for failed requests
    this.initializeCompliance();
  }

  /**
   * Initialize HIPAA compliance measures
   */
  initializeCompliance() {
    // Start session audit
    this.logAuditEvent({
      action: 'SESSION_START',
      details: 'User session initiated',
      level: 'INFO'
    });

    // Set up automatic session timeout (15 minutes of inactivity)
    this.setupSessionTimeout();

    // Monitor for PHI in console/debug logs
    this.monitorConsoleForPHI();

    // Best-effort flush of any queued audit events when the tab closes
    this.setupQueueFlushOnUnload();
  }

  /**
   * Generate unique session ID for audit trail
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Log audit events for HIPAA compliance - SERVER-SIDE ONLY
   * @param {Object} event - Audit event details
   */
  logAuditEvent(event) {
    const auditEntry = {
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.getCurrentUserId(),
      action: event.action,
      details: event.details,
      level: event.level || 'INFO',
      userAgent: navigator.userAgent
    };

    // HIPAA COMPLIANCE: Send ONLY to server - NO client-side storage or logging
    this.sendToSecureLogger(auditEntry);
    
    // NO console logging in production for HIPAA compliance
    if (process.env.NODE_ENV === 'development') {
      console.log('[HIPAA AUDIT - DEV ONLY]', this.sanitizeForLogging(auditEntry));
    }
  }

  /**
   * Log PHI access events
   * @param {string} action - Type of PHI access
   * @param {string} patientId - Patient identifier (hashed)
   * @param {string} dataType - Type of data accessed
   */
  logPHIAccess(action, patientId = null, dataType = 'clinical_note') {
    // No patient identifier is ever included in audit details: PHI is neither
    // hashed nor logged. Only the action and the generic data type are recorded.
    this.logAuditEvent({
      action: `PHI_${action.toUpperCase()}`,
      details: `${action} ${dataType}`,
      level: 'SECURITY'
    });
  }

  /**
   * Log authentication events
   * @param {string} action - Auth action (LOGIN, LOGOUT, FAILED_LOGIN)
   * @param {string} email - User email
   */
  logAuthEvent(action, email) {
    this.logAuditEvent({
      action: `AUTH_${action.toUpperCase()}`,
      details: `User authentication: ${action.toLowerCase()} for ${this.maskEmail(email)}`,
      level: action === 'FAILED_LOGIN' ? 'WARNING' : 'INFO'
    });
  }

  /**
   * Sanitize data to remove potential PHI
   * @param {any} data - Data to sanitize
   * @returns {any} Sanitized data
   */
  sanitizeForLogging(data) {
    if (typeof data !== 'object' || data === null) {
      return this.scrubPHI(String(data));
    }

    const sanitized = Array.isArray(data) ? [] : {};
    
    for (const [key, value] of Object.entries(data)) {
      // Skip sensitive fields
      if (this.isSensitiveField(key)) {
        sanitized[key] = '[REDACTED-PHI]';
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeForLogging(value);
      } else {
        sanitized[key] = this.scrubPHI(String(value));
      }
    }

    return sanitized;
  }

  /**
   * Check if field contains potential PHI
   * @param {string} fieldName - Field name to check
   * @returns {boolean} True if field is sensitive
   */
  isSensitiveField(fieldName) {
    const sensitiveFields = [
      'ssn', 'social_security', 'dob', 'date_of_birth', 'birthdate',
      'phone', 'mobile', 'address', 'street', 'city', 'state', 'zip',
      'email', 'patient_name', 'mrn', 'medical_record', 'diagnosis',
      'medication', 'treatment', 'note_content', 'transcription'
    ];
    
    return sensitiveFields.some(field => 
      fieldName.toLowerCase().includes(field)
    );
  }

  /**
   * Scrub potential PHI from strings
   * @param {string} text - Text to scrub
   * @returns {string} Scrubbed text
   */
  scrubPHI(text) {
    if (!text) return text;
    
    // Remove potential SSNs, phone numbers, emails, dates
    return text
      .replace(/\b\d{3}-?\d{2}-?\d{4}\b/g, '[SSN-REDACTED]')
      .replace(/\b\d{3}-?\d{3}-?\d{4}\b/g, '[PHONE-REDACTED]')
      .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL-REDACTED]')
      .replace(/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/g, '[DATE-REDACTED]')
      .replace(/\b\d{4}-\d{2}-\d{2}\b/g, '[DATE-REDACTED]');
  }

  /**
   * Mask email for logging
   * @param {string} email - Email to mask
   * @returns {string} Masked email
   */
  maskEmail(email) {
    if (!email) return '[NO-EMAIL]';
    const [name, domain] = email.split('@');
    if (!domain) return '[INVALID-EMAIL]';
    return `${name.charAt(0)}***@${domain}`;
  }

  /**
   * Set up automatic session timeout for HIPAA compliance
   */
  setupSessionTimeout() {
    const TIMEOUT_MINUTES = 15;
    const TIMEOUT_MS = TIMEOUT_MINUTES * 60 * 1000;

    let timeoutId;
    let lastActivity = Date.now();

    const resetTimeout = () => {
      clearTimeout(timeoutId);
      lastActivity = Date.now();
      
      timeoutId = setTimeout(() => {
        this.logAuditEvent({
          action: 'SESSION_TIMEOUT',
          details: `Automatic logout after ${TIMEOUT_MINUTES} minutes of inactivity`,
          level: 'SECURITY'
        });
        
        // Trigger logout
        window.dispatchEvent(new CustomEvent('hipaa-session-timeout'));
      }, TIMEOUT_MS);
    };

    // Monitor user activity
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
      document.addEventListener(event, resetTimeout, { passive: true });
    });

    resetTimeout(); // Start the timer
  }

  /**
   * Best-effort flush of queued audit events when the page is being closed/hidden.
   * Uses navigator.sendBeacon so in-flight entries survive a tab close. These flushed
   * entries post to the open endpoint (no token is available at unload) and are
   * recorded server-side as unverified.
   */
  setupQueueFlushOnUnload() {
    const flush = () => {
      if (!this.auditQueue.length || !navigator.sendBeacon) return;
      const baseUrl = process.env.REACT_APP_AUDIT_API_URL ||
                      window.CHARTARA_CONFIG?.AUDIT_API_URL ||
                      'https://rg4y4dnzia.execute-api.us-east-2.amazonaws.com/prod/audit';
      const pending = this.auditQueue.splice(0, this.auditQueue.length);
      for (const entry of pending) {
        try {
          const blob = new Blob([JSON.stringify(entry)], { type: 'text/plain' });
          navigator.sendBeacon(baseUrl, blob);
        } catch (e) {
          // best-effort: the page is unloading, nothing more we can do
        }
      }
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
  }

  /**
   * Monitor console for potential PHI leaks
   */
  monitorConsoleForPHI() {
    const isProduction = process.env.NODE_ENV === 'production';
    const noop = () => {};

    // In production, suppress verbose logs entirely so that no PHI (e.g., a voice
    // transcript or note passed as a log argument anywhere in the app) can ever
    // reach the browser console. In development they are sanitized for debugging.
    if (isProduction) {
      console.log = noop;
      console.info = noop;
      console.debug = noop;
    } else {
      const originalLog = console.log;
      console.log = (...args) => originalLog.apply(console, args.map(arg => this.sanitizeForLogging(arg)));
    }

    // Keep error/warn for diagnostics, but sanitize structured PHI patterns.
    const originalError = console.error;
    const originalWarn = console.warn;
    console.error = (...args) => originalError.apply(console, args.map(arg => this.sanitizeForLogging(arg)));
    console.warn = (...args) => originalWarn.apply(console, args.map(arg => this.sanitizeForLogging(arg)));
  }

  /**
   * Get current user ID for audit trail
   */
  getCurrentUserId() {
    try {
      const userStr = localStorage.getItem('currentUser');
      const user = userStr ? JSON.parse(userStr) : null;
      return user?.id || 'anonymous';
    } catch {
      return 'anonymous';
    }
  }

  /**
   * Get client IP (best effort)
   */
  getClientIP() {
    // In production, this should come from server-side headers
    return 'client-side-unknown';
  }

  /**
   * Send audit log to secure server-side logging service
   * @param {Object} auditEntry - Audit log entry
   */
  async sendToSecureLogger(auditEntry) {
    try {
      const baseUrl = process.env.REACT_APP_AUDIT_API_URL ||
                      window.CHARTARA_CONFIG?.AUDIT_API_URL ||
                      'https://rg4y4dnzia.execute-api.us-east-2.amazonaws.com/prod/audit';

      // Bind the event to a verified identity when authenticated: include the Cognito
      // ID token in the request BODY (not a header) so the request stays a simple
      // application/json POST with no added CORS-preflight requirements. The audit
      // Lambda verifies the token and derives the userId from its claims (never the
      // client-supplied userId). Pre-authentication events (e.g., SESSION_START,
      // FAILED_LOGIN) omit the token and are recorded server-side as unverified.
      const url = baseUrl;
      let payload = auditEntry;
      try {
        const session = await fetchAuthSession();
        const idToken = session?.tokens?.idToken?.toString();
        if (idToken) {
          payload = { ...auditEntry, idToken };
        }
      } catch (e) {
        // Not authenticated yet — pre-auth events post without a token.
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Process any queued audit logs on successful send
      if (this.auditQueue.length > 0) {
        await this.processAuditQueue();
      }

    } catch (error) {
      // Queue the audit entry for retry - but NO console logging in production
      this.auditQueue.push({
        ...auditEntry,
        retryCount: (auditEntry.retryCount || 0) + 1,
        lastError: error.message,
        queuedAt: new Date().toISOString()
      });

      // Only log errors in development
      if (process.env.NODE_ENV === 'development') {
        console.error('[HIPAA AUDIT ERROR - DEV ONLY]', error);
      }

      // Prevent queue from growing too large
      if (this.auditQueue.length > 100) {
        this.auditQueue = this.auditQueue.slice(-50); // Keep only last 50 entries
      }
    }
  }

  /**
   * Process queued audit logs
   */
  async processAuditQueue() {
    const queueCopy = [...this.auditQueue];
    this.auditQueue = [];

    for (const queuedEntry of queueCopy) {
      // Only retry a limited number of times
      if (queuedEntry.retryCount < 3) {
        await this.sendToSecureLogger(queuedEntry);
      } else if (process.env.NODE_ENV === 'development') {
        console.warn('[HIPAA AUDIT - DEV ONLY] Dropping audit entry after 3 retries:', queuedEntry.action);
      }
    }
  }

  /**
   * Clear session data on logout
   */
  clearSession() {
    this.logAuditEvent({
      action: 'SESSION_END',
      details: 'User session ended',
      level: 'INFO'
    });

    // Clear any cached PHI
    localStorage.removeItem('voiceRecordings');
    localStorage.removeItem('noteTemplates');
    localStorage.removeItem('recentNotes');
    
    // Clear audit queue (no persistent client-side storage)
    this.auditQueue = [];
  }

  /**
   * Get audit logs for compliance reporting - SERVER-SIDE ONLY
   * @returns {Array} Empty array - logs are stored server-side only
   */
  getAuditLogs() {
    // HIPAA COMPLIANCE: No client-side audit log storage
    if (process.env.NODE_ENV === 'development') {
      console.warn('[HIPAA COMPLIANCE] Audit logs are stored server-side only. Use admin interface to access logs.');
    }
    return []; // Always return empty - logs are server-side only
  }
}

// Create singleton instance
export const hipaaService = new HIPAAComplianceService();
export default hipaaService;